import { useState } from 'react'
import toast from 'react-hot-toast'
import dayjs from 'dayjs'

import EyeOutlinedIcon from '@ant-design/icons/EyeOutlined'
import FileOutlinedIcon from '@ant-design/icons/FileOutlined'
import MoreOutlinedIcon from '@ant-design/icons/MoreOutlined'

import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import Chip from '@mui/material/Chip'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Stack from '@mui/material/Stack'
import DialogTitle from '@components/_mui/DialogTitle'
import IconButton from '@components/_mui/IconButton'

import { isImage, isPdf } from '@shared/utils'

import { FileRelatedUser } from '@components/FileUploader'
import ImageFullScreenViewer from '@components/ImageFullScreenViewer'
import { PdfModal } from '@components/PdfViewer'
import RenderControl from '@components/RenderControl'

import { usePatientDocuments, useTaskFileLink } from './TaskAttachments.hooks'

export default function LinkDocumentDialog({ task, linkedIds, open, onClose }) {
  return (
    <Dialog fullWidth maxWidth="sm" open={open} onClose={onClose} scroll="paper">
      {open && <Content task={task} linkedIds={linkedIds} onClose={onClose} />}
    </Dialog>
  )
}

function Content({ task, linkedIds, onClose }) {
  const [selected, setSelected] = useState([])

  const link = useTaskFileLink(task.id)
  const { data, isPending, fetchNextPage, hasNextPage, isFetchingNextPage } = usePatientDocuments(task.patientId)

  const documents = data ?? []

  const toggle = (document) =>
    setSelected((prev) => (prev.some((d) => d.id === document.id) ? prev.filter((d) => d.id !== document.id) : [...prev, document]))

  const handleLink = () =>
    link.mutateAsync(selected).then(({ linked, failed }) => {
      if (failed.length === 0) {
        toast.success(linked > 1 ? `${linked} documents were linked` : 'Document was linked')
        onClose()
        return
      }
      setSelected(failed.map(({ document }) => document))
      toast.error(failed.map(({ document, reason }) => `${document.name || document.filename}: ${reason}`).join('\n'))
    })

  return (
    <>
      <DialogTitle onClose={onClose}>Link patient document</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {task.patient && <FileRelatedUser user={task.patient} />}
          <RenderControl loading={isPending} isEmpty={documents.length === 0} emptyTitle="Patient does not have any documents">
            <Stack spacing={1}>
              <List disablePadding sx={styles.list}>
                {documents.map((document) => {
                  const linked = linkedIds.has(document.id)
                  const checked = selected.some((d) => d.id === document.id)
                  return (
                    <ListItemButton key={document.id} disabled={linked} selected={checked} onClick={() => toggle(document)} sx={styles.row}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        <Checkbox edge="start" checked={checked || linked} disabled={linked} tabIndex={-1} disableRipple />
                      </ListItemIcon>
                      <ListItemIcon sx={{ minWidth: 32, color: 'text.secondary' }}>
                        <FileOutlinedIcon style={{ fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText
                        primary={document.name || document.filename}
                        secondary={[
                          document.documenttype,
                          (document.createdAt ?? document.created) && dayjs(document.createdAt ?? document.created).format('LL'),
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                        slotProps={{ primary: { noWrap: true, variant: 'subtitle1' }, secondary: { noWrap: true, variant: 'subtitle2' } }}
                      />
                      <PreviewButton document={document} />
                      {linked && <Chip size="small" variant="light" label="Linked" />}
                    </ListItemButton>
                  )
                })}
              </List>
              {hasNextPage && (
                <Button
                  fullWidth
                  loading={isFetchingNextPage}
                  onClick={() => fetchNextPage()}
                  endIcon={<MoreOutlinedIcon rotate={90} />}
                  loadingPosition="end"
                  sx={{ textTransform: 'none' }}
                >
                  more
                </Button>
              )}
            </Stack>
          </RenderControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="error" disabled={link.isPending} onClick={onClose}>
          Cancel
        </Button>
        <Button variant="contained" loading={link.isPending} disabled={selected.length === 0} onClick={handleLink}>
          {selected.length > 1 ? `Link ${selected.length} documents` : 'Link document'}
        </Button>
      </DialogActions>
    </>
  )
}

function PreviewButton({ document }) {
  const [open, setOpen] = useState(false)

  const button = (onClick) => (
    <IconButton
      size="small"
      color="secondary"
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
    >
      <EyeOutlinedIcon />
    </IconButton>
  )

  if (isImage(document.mimetype)) {
    return <ImageFullScreenViewer image={document.url}>{(openViewer) => button(openViewer)}</ImageFullScreenViewer>
  }

  if (isPdf(document.mimetype)) {
    return (
      <>
        {button(() => setOpen(true))}
        <PdfModal
          initialScale={1.2}
          url={document.url}
          filename={document.filename}
          title={document.name || document.filename}
          open={open}
          onClose={() => setOpen(false)}
        />
      </>
    )
  }

  return button(() => window.open(document.url, '_blank'))
}

const styles = {
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0.5,
  },
  row: {
    py: 0.5,
    borderRadius: 1,
  },
}
